/* eslint-disable no-underscore-dangle */
import type * as zod from 'zod';

import * as log from '@sb/log';
import {
  ControllerType,
  type ExternalControllerStateMsg,
} from '@sb/messenger/auto_generated/messenger/external_control_bot_service';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import type {
  StepAction,
  StepPlayArguments,
} from '@sb/routine-runner/Step/Step';
import Step from '@sb/routine-runner/Step/Step';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import type { MiraiController } from '../../implementation/MiraiController';

import Arguments from './Arguments';
import Variables from './Variables';

const ns = log.namespace('MiraiRunSkillStep');

const TAKE_CONTROL_TIMEOUT_MS = 5_000;
const RELEASE_CONTROL_TIMEOUT_MS = 300_000;

type ArgumentsType = zod.infer<typeof Arguments>;
type VariablesType = zod.infer<typeof Variables>;

type StateWaitResult =
  | 'completed'
  | 'take-timeout'
  | 'release-timeout'
  | 'cancelled';
type SkillLifecycleResult = StateWaitResult;

export class MiraiRunSkillStep extends Step<ArgumentsType, VariablesType> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  private controlSource: string | null = null;

  private clientId: string = `mirai-run-skill-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

  private stopped = false;

  private lifecyclePhase: 'idle' | 'waiting-for-take' | 'waiting-for-release' =
    'idle';

  private activeTimeout: ReturnType<typeof setTimeout> | null = null;

  private activeResolve: ((result: StateWaitResult) => void) | null = null;

  public initializeVariableState(): void {
    this.variables = { status: 'idle' };
  }

  /**
   * No step self timeout for this step: the step sleeps for an extended
   * period while the MIRAI controller runs a skill, so the default 250ms
   * self-timeout would always trigger a spurious error log.
   */
  public hasSelfTimeout(): boolean {
    return false;
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    this.stopped = false;

    if (!getFeatureFlagSync('miraiRunSkill', false)) {
      await fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'MiraiRunSkill',
        },
        failureReason:
          'MiraiRunSkill step requires the miraiRunSkill feature flag to be enabled',
      });

      return;
    }

    if (!getFeatureFlagSync('useControlSourceManager', false)) {
      await fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'MiraiRunSkill',
        },
        failureReason:
          'MiraiRunSkill step requires the Control Source Manager feature flag to be enabled',
      });

      return;
    }

    try {
      const device = this.routineContext.equipment.getDeviceById(
        this.args.miraiControllerId,
      ) as MiraiController | undefined;

      if (!device) {
        await fail({
          failure: {
            kind: FailureKind.StepPlayFailure,
            stepKind: 'MiraiRunSkill',
          },
          failureReason: `MIRAI controller not found (id: ${this.args.miraiControllerId})`,
        });

        return;
      }

      const state = await device.updateAsync();

      if (!state.isConnected) {
        await fail({
          failure: {
            kind: FailureKind.StepPlayFailure,
            stepKind: 'MiraiRunSkill',
          },
          failureReason: 'MIRAI controller is not connected',
        });

        return;
      }

      // Subscribe early so the ZMQ SUB socket has time to connect before
      // external-control-bot publishes the state change.
      this.subscribeToExternalControllerState();

      const controlSource = this.getControlSource();
      await this.requestControlSource(controlSource);

      // ZMQ PUB/SUB needs time for the subscription handshake to complete.
      // Without this delay, messages published shortly after connecting are
      // silently dropped ("slow joiner" problem). 500ms is conservative.
      await new Promise((resolve) => setTimeout(resolve, 500));

      if (this.stopped) {
        return;
      }

      this.variables = { status: 'running' };

      log.info(
        ns`play`,
        'Starting skill lifecycle wait (5s take-control timeout)',
      );

      const skillLifecycle = this.waitForSkillLifecycle();

      await device.executeCommand({
        kind: 'MiraiControllerCommand',
        skillId: this.args.skillId,
      });

      const result = await skillLifecycle;

      if (this.stopped || result === 'cancelled') {
        return;
      }

      if (result === 'take-timeout') {
        this.variables = { status: 'failed' };

        await fail({
          failure: {
            kind: FailureKind.StepPlayFailure,
            stepKind: 'MiraiRunSkill',
          },
          failureReason: 'MIRAI skill never took control within 5 seconds',
        });

        return;
      }

      if (result === 'release-timeout') {
        log.warn(
          ns`waitForControlReleased`,
          'Timed out waiting for control released after 5 minutes — treating as completed',
        );
      }

      this.variables = { status: 'completed' };
    } catch (error) {
      log.error(ns`play`, 'Error in MiraiRunSkill step', { error });

      this.variables = { status: 'failed' };

      await fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'MiraiRunSkill',
        },
        failureReason: `MiraiRunSkill step failed: ${(error as Error)?.message}`,
        error: error as Error,
      });
    } finally {
      this.cancelWait();
      this.routineContext.externalControlBotConnector?.unsubscribeFromExternalControllerState();
      await this.releaseControlSource();
    }
  }

  public async _pause(_args: StepAction): Promise<void> {
    this.stopped = true;
    this.cancelWait();
    this.routineContext.externalControlBotConnector?.unsubscribeFromExternalControllerState();
    await this.releaseControlSource();
  }

  public async _stop(): Promise<void> {
    this.stopped = true;
    this.cancelWait();
    this.routineContext.externalControlBotConnector?.unsubscribeFromExternalControllerState();
    await this.releaseControlSource();
  }

  /**
   * Subscribe to ExternalControllerState early so the ZMQ SUB socket has
   * time to complete the handshake before external-control-bot publishes.
   * Call this well before executeCommand.
   */
  private subscribeToExternalControllerState(): void {
    const connector = this.routineContext.externalControlBotConnector;

    if (!connector) {
      log.warn(
        ns`subscribeToExternalControllerState`,
        'No externalControlBotConnector on routineContext — cannot subscribe',
      );

      return;
    }

    connector.subscribeToExternalControllerState(
      (msg: ExternalControllerStateMsg) => {
        log.info(ns`onExternalControllerState`, 'Received state', {
          hasActiveController: msg.hasActiveController,
          controllerType: msg.controllerType,
          lifecyclePhase: this.lifecyclePhase,
        });

        if (
          this.lifecyclePhase === 'waiting-for-take' &&
          msg.hasActiveController &&
          msg.controllerType === ControllerType.CONTROLLER_TYPE_UDP
        ) {
          log.info(ns`waitForSkillLifecycle`, 'MIRAI skill took UDP control');
          this.lifecyclePhase = 'waiting-for-release';
          this.setPhaseTimeout(RELEASE_CONTROL_TIMEOUT_MS, 'release-timeout');
        } else if (
          this.lifecyclePhase === 'waiting-for-release' &&
          !msg.hasActiveController
        ) {
          log.info(ns`waitForSkillLifecycle`, 'MIRAI skill released control');
          this.resolveLifecycle('completed');
        }
      },
    );
  }

  /**
   * Start the take-control timeout and return a promise that resolves when
   * the full skill lifecycle completes. The subscription must already be
   * active (call subscribeToExternalControllerState first).
   */
  private waitForSkillLifecycle(): Promise<SkillLifecycleResult> {
    if (!this.routineContext.externalControlBotConnector) {
      log.warn(
        ns`waitForSkillLifecycle`,
        'No ExternalControlBotConnector available, resolving immediately',
      );

      return Promise.resolve('completed');
    }

    // Reset phase for this execution. The subscription callback will
    // advance it as state events arrive.
    this.lifecyclePhase = 'waiting-for-take';

    return new Promise<SkillLifecycleResult>((resolve) => {
      this.activeResolve = resolve as (result: StateWaitResult) => void;
      this.setPhaseTimeout(TAKE_CONTROL_TIMEOUT_MS, 'take-timeout');
    });
  }

  private setPhaseTimeout(ms: number, result: SkillLifecycleResult): void {
    if (this.activeTimeout) {
      clearTimeout(this.activeTimeout);
    }

    this.activeTimeout = setTimeout(() => {
      this.resolveLifecycle(result);
    }, ms);
  }

  private resolveLifecycle(result: SkillLifecycleResult): void {
    this.lifecyclePhase = 'idle';

    if (this.activeTimeout) {
      clearTimeout(this.activeTimeout);
      this.activeTimeout = null;
    }

    if (this.activeResolve) {
      this.activeResolve(result);
      this.activeResolve = null;
    }
  }

  private cancelWait(): void {
    this.resolveLifecycle('cancelled');
  }

  /**
   * Derive the CSM control source from feature flags.
   * Mirrors ro1_zmq_manager._derive_control_source().
   */
  private getControlSource(): string {
    const zmqPoseMigration = getFeatureFlagSync('zmqPoseMigration', false);
    const complianceMode = getFeatureFlagSync('complianceMode', false);

    if (complianceMode) {
      return 'COMPLIANCE_JOINT_TRAJECTORY';
    }

    if (zmqPoseMigration) {
      return 'EXTERNAL_POSE';
    }

    return 'ROS_POSE';
  }

  private async requestControlSource(source: string): Promise<void> {
    this.controlSource = source;

    log.info(ns`requestControlSource`, 'Requesting control source', {
      source,
      clientId: this.clientId,
    });

    const result =
      await this.routineContext.armControlBotConnector?.SetControlSource({
        value: source,
        clientId: this.clientId,
      });

    if (result?.error) {
      throw new Error(
        `Failed to acquire control source: ${result.error.message}`,
      );
    }
  }

  private async releaseControlSource(): Promise<void> {
    if (!this.controlSource) {
      return;
    }

    const source = this.controlSource;
    this.controlSource = null;

    log.info(ns`releaseControlSource`, 'Releasing control source', {
      source,
      clientId: this.clientId,
    });

    await this.routineContext.armControlBotConnector?.ReleaseControlFromSource({
      value: source,
      clientId: this.clientId,
    });
  }
}
